import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Pagination } from '@/components/shared/Pagination';
import { fmtMoeda } from '@/lib/utils/formatters';
import { useSimplesNacional } from '@/contexts/SimplesNacionalContext';
import { useAlterarStatusSegregacao } from '@/services/SimplesNacional/Segregacao/hooks/useAlterarStatusSegregacao';
import {
  STATUS_PRODUTO_LABEL,
  type SegregacaoRev,
  type StatusProduto,
} from '@/services/SimplesNacional/Segregacao/api/types';

interface ProdutosEmRevisaoTableProps {
  data: SegregacaoRev[];
}

export function ProdutosEmRevisaoTable({ data }: ProdutosEmRevisaoTableProps) {
  const [busca, setBusca] = useState('');
  const [pagina, setPagina] = useState(1);
  const [itensPorPagina, setItensPorPagina] = useState(25);

  const { cnpjContribuinte } = useSimplesNacional();
  const { mutate: alterarStatus, isPending } = useAlterarStatusSegregacao();

  const filtrados = busca
    ? data.filter(
        item =>
          item.Produto?.toLowerCase().includes(busca.toLowerCase()) ||
          item.Descricao?.toLowerCase().includes(busca.toLowerCase()) ||
          item.NCM?.includes(busca)
      )
    : data;

  const totalPaginas = Math.max(1, Math.ceil(filtrados.length / itensPorPagina));
  const paginaAtual = Math.min(pagina, totalPaginas);
  const inicio = (paginaAtual - 1) * itensPorPagina;
  const itens = filtrados.slice(inicio, inicio + itensPorPagina);

  if (data.length === 0) {
    return (
      <p className="px-4 py-6 text-sm text-muted-foreground text-center">
        Nenhum produto em revisão encontrado.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <div className="px-4 pt-2">
        <Input
          placeholder="Buscar por produto, descrição ou NCM..."
          value={busca}
          onChange={e => {
            setBusca(e.target.value);
            setPagina(1);
          }}
          className="max-w-sm h-8 text-xs"
        />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b bg-muted/40">
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Produto</th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Descrição</th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Ref. SPED</th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">NCM</th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Status</th>
              <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">
                Valor Total
              </th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Data</th>
              <th className="text-center px-4 py-2.5 font-medium text-muted-foreground">Ação</th>
            </tr>
          </thead>
          <tbody>
            {itens.map((item, i) => {
              const statusInfo = STATUS_PRODUTO_LABEL[item.Status as StatusProduto];
              const podeRevisar = !!item.ChaveAcessoDOC && !!cnpjContribuinte;
              return (
                <tr key={i} className="border-b last:border-0 hover:bg-muted/20 transition-colors">
                  <td className="px-4 py-2 max-w-[130px] truncate" title={item.Produto}>
                    {item.Produto || '—'}
                  </td>
                  <td className="px-4 py-2 max-w-[180px] truncate" title={item.Descricao}>
                    {item.Descricao || '—'}
                  </td>
                  <td
                    className="px-4 py-2 max-w-[130px] truncate text-muted-foreground"
                    title={item.ProdutoReferencialSPED}
                  >
                    {item.ProdutoReferencialSPED || '—'}
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">{item.NCM || '—'}</td>
                  <td className="px-4 py-2">
                    {statusInfo ? (
                      <Badge className={`${statusInfo.color} text-white text-[10px] px-1.5 py-0`}>
                        {statusInfo.label}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">{item.Status || '—'}</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums font-medium">
                    {fmtMoeda(item.ValTot)}
                  </td>
                  <td className="px-4 py-2 text-muted-foreground whitespace-nowrap">
                    {item.DataInclusao || '—'}
                  </td>
                  <td className="px-4 py-2 text-center">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 text-[10px] px-2"
                      disabled={!podeRevisar || isPending}
                      onClick={() =>
                        alterarStatus({
                          CNPJContribuinte: cnpjContribuinte!,
                          ChaveAcesso: item.ChaveAcessoDOC!,
                          Status: '3',
                        })
                      }
                    >
                      {isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Revisar'}
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="px-4 pb-2 flex items-center justify-between gap-4 text-xs text-muted-foreground">
        <span>{filtrados.length} produto(s)</span>
        {totalPaginas > 1 && (
          <Pagination
            currentPage={paginaAtual}
            totalPages={totalPaginas}
            onPageChange={setPagina}
            itensPorPagina={itensPorPagina}
            onItensPorPaginaChange={v => {
              setItensPorPagina(v);
              setPagina(1);
            }}
          />
        )}
      </div>
    </div>
  );
}
